import i18n from 'i18n'
import { all, put, select, takeLatest } from 'redux-saga/effects'

import * as actionTypes from './action-types'
import * as stepperActionTypes from '../stepper/action-types'
import * as onlineChannelActionTypes from '../onlineChannel/action-types'
import * as orderActionTypes from '../order/action-types'
import * as thankYouActionTypes from '../thankYou/action-types'
import * as loginActionTypes from '../login/action-types'
import * as dashboardActionTypes from '../dashboard/action-types'

import { sendGAEvent, sendGAPageView } from './actions'
import { getActiveStep, getAllStepsValid } from 'services/stepper/selectors'
import { getSelectedRate, getSelectedService, getIsOnlyToppings } from 'services/products/selectors'
import { getSelectedSingleToppings } from 'services/toppings/selectors'
import { getIsOnlineChannel, getIsPhoneChannel, getSelectedChannel } from 'services/operator/selectors'
import { getCupsSearchByCups } from 'services/cupsSearcher/selectors'

import { getRouterPathname, getRouterQueries } from './selectors'
import {
  mapProductsAndServices,
  mapServicesNames,
  trackEvent,
  trackPage,
  mapCheckoutServices,
  getCheckoutObjectRate,
  getEcommerceData,
} from './business'
import { GA_EVENT_NAMES, PHONE_CHANNELS, SPF_NAME, GA_FILTERS_SERVICE, ELECTRICITY } from 'utils/constants'
import { getCRMId } from 'services/order/selectors'
import { get, remove, set } from 'services/sessionStorage'
import isEmptyObject from '@zatopek/core/src/utils/isEmptyObject'
import { getCompany, getSupplyAddress } from 'services/customer/selectors'
import { getSignParams } from 'utils/commons'

function* sendPageViewSaga(payload) {
  const areAllStepsCompleted = yield select(getAllStepsValid)
  const product = yield select(getSelectedRate)
  const activeStep = yield select(getActiveStep)
  const services = yield select(getSelectedSingleToppings)
  const isOnlineChannel = yield select(getIsOnlineChannel)
  const isOnlyToppings = yield select(getIsOnlyToppings)
  const isPhoneChannel = yield select(getIsPhoneChannel)
  const selectedChannel = yield select(getSelectedChannel)
  const pathname = yield select(getRouterPathname)
  const queries = yield select(getRouterQueries)
  const selectedService = yield select(getSelectedService) || ELECTRICITY
  /**
   *  Right now we only enter in COL from selecting product
   * TODO: add variable when we can enter the funnel through service selection to measure
   * cross selling.
   * cross selling criteria:
   * - Enter through a product and select service
   * - Enter through a service and select product (not possible yet)
   */
  const isCrossSelling = isOnlineChannel && !isEmptyObject(services || {})
  const params = {
    sel: queries?.sel || '',
    sva: queries?.sva || '',
    src: queries?.src || '',
    id: queries?.id || '',
    tipo: queries?.tipo || '',
    Origen: queries?.origen || '',
  }
  let virtualPageStep = payload?.payload?.stepId || activeStep
  const mappedProductsAndServices = mapProductsAndServices({ product, services })
  const hasSPF = services && mapServicesNames(services)?.includes(SPF_NAME)
  const formattedData = {
    ...params,
    'User Type': isOnlineChannel ? 'noasistido' : 'asistido',
    assisted: isPhoneChannel ? PHONE_CHANNELS[selectedChannel?.type] : '',
    paginaVirtual: `${pathname}/paso-${virtualPageStep < 0 ? 'validacion' : virtualPageStep + 1}`, // active step < 0 means all steps are closed => validation step
    mantenimiento: services ? mapServicesNames(services).join(',') : '',
    Tarifa: product?.name || '',
    Producto:
      isOnlyToppings || (!product && !services)
        ? i18n.t('common.contractOnlyMaintenance')
        : product || services
        ? mappedProductsAndServices
        : '',
    'subcategoría del asistido': !isOnlineChannel ? selectedChannel.name : '',
    spf: hasSPF || false,
    dist: GA_FILTERS_SERVICE[selectedService],
    xs: isCrossSelling,
    ventanaAbierta: isOnlineChannel && (virtualPageStep <= 0 || (virtualPageStep === 2 && activeStep === 0)) ? false : true,
  }

  if (areAllStepsCompleted) {
    set('transactionPageView', JSON.stringify(formattedData))
  }

  // TODO isOnlyToppings define activeStep === -2 and don't send pageView
  if (virtualPageStep !== -2) {
    trackPage(formattedData)
    yield put(sendGAPageView())
  }
}

function* sendGAEventSaga({ payload: { eventCallbackName, data } }) {
  const byCups = yield select(getCupsSearchByCups)
  if (eventCallbackName === 'checkoutEvent' && byCups && data?.ecommerce?.checkout?.products) {
    data.ecommerce.checkout.products = data.ecommerce.checkout.products.map((product) => ({
      ...product,
      ...(product.id.replace('/middleware/families/', '') in byCups
        ? {
            byCups: Object.values(byCups[product.id.replace('/middleware/families/', '')]).includes(true),
            byAddress: Object.values(byCups[product.id.replace('/middleware/families/', '')]).includes(false),
          }
        : {}),
    }))
  }
  trackEvent(eventCallbackName, data)
}

function* sendGACheckoutEventSaga() {
  const rate = yield select(getSelectedRate)
  const services = yield select(getSelectedSingleToppings)
  const step = yield select(getActiveStep)
  const areAllStepsCompleted = yield select(getAllStepsValid)
  const arrayServices = []
  const rateArray = []
  if (rate) {
    rateArray.push(getCheckoutObjectRate(rate, step))
  }
  if (services) {
    arrayServices.push(...mapCheckoutServices(services, step))
  }
  const productsAndServices = [...rateArray, ...arrayServices].map((item, position) => ({ ...item, position }))
  const result = {
    ecommerce: getEcommerceData(productsAndServices, step + 1, 'checkout'),
  }

  if (areAllStepsCompleted) {
    set('transactionCheckout', JSON.stringify(result))
  }

  if (productsAndServices.length) {
    yield put(sendGAEvent(GA_EVENT_NAMES.checkoutEvent, result))
  }
}

function* sendGASignEventSaga() {
  const rate = yield select(getSelectedRate)
  const services = yield select(getSelectedSingleToppings)
  const CRMid = yield select(getCRMId)
  const step = yield select(getActiveStep)
  const supplyAddress = yield select(getSupplyAddress)
  const company = yield select(getCompany)
  const selectedCompanyCode = company?.code || rate?.company?.code
  const arrayServices = []
  const rateArray = []

  const paramsSign = getSignParams(rate, services, supplyAddress, selectedCompanyCode)
  trackPage(paramsSign)
  yield put(sendGAPageView())

  if (rate) {
    rateArray.push(getCheckoutObjectRate(rate))
  }
  if (services) {
    const servicesArray = mapCheckoutServices(services)
    arrayServices.push(...servicesArray)
  }

  const products = [...rateArray, ...arrayServices]
  if (products.length) {
    const revenue = products.reduce((acc, value) => acc + value.price, 0)
    const productsToSend = [...rateArray, ...arrayServices].map((item, position) => ({
      ...item,
      revenue: revenue.toFixed(2),
      transactionId: CRMid || '',
      position,
    }))
    const result = {
      event: 'event.ecommerce',
      ecommerce: getEcommerceData(productsToSend, step, 'checkout'),
    }

    yield put(sendGAEvent(GA_EVENT_NAMES.checkoutEvent, result))
    const byCups = result.ecommerce?.checkout?.products?.some(({ byCups }) => byCups)
    const byAddress = result.ecommerce?.checkout?.products?.some(({ byAddress }) => byAddress)
    if (byCups || byAddress) {
      const eventLabel = byCups && !byAddress ? 'byCups' : byAddress && !byCups ? 'byAddress' : 'byCupsAndAddress'
      yield put(
        sendGAEvent(GA_EVENT_NAMES.checkoutEvent, {
          eventLabel,
          eventCategory: 'ecommerce',
          eventAction: 'sign-product',
        })
      )
    }
  }
}

function* thankYouPageEventSaga() {
  const rate = yield select(getSelectedRate)
  const services = yield select(getSelectedSingleToppings)
  const supplyAddress = yield select(getSupplyAddress)
  const company = yield select(getCompany)
  const selectedCompanyCode = company?.code || rate?.company?.code

  const checkoutData = JSON.parse(get('transactionCheckout'))
  const pageViewData = JSON.parse(get('transactionPageView'))
  const pathname = yield select(getRouterPathname)

  if (pageViewData && !isEmptyObject(pageViewData)) {
    pageViewData.paginaVirtual = `${pathname}/paso-compra`
  }

  if (checkoutData && !isEmptyObject(checkoutData)) {
    checkoutData.ecommerce.checkout.actionField.step = 'compra'
  }

  const paramsSign = getSignParams(rate, services, supplyAddress, selectedCompanyCode)

  // PageView of thank yoy page
  trackPage({ ...pageViewData, ...paramsSign })
  yield put(sendGAPageView())
  yield put(sendGAEvent(GA_EVENT_NAMES.checkoutEvent, checkoutData))
  remove('transactionCheckout')
  remove('transactionPageView')
}

// eslint-disable-next-line require-yield
function* loginPageEventSaga() {
  // const pathname = yield select(getRouterPathname)
  const pageViewData = {
    'User Type': 'asistido',
  }
  trackPage(pageViewData)
}

// eslint-disable-next-line require-yield
function* dashboardPageEventSaga() {
  // const pathname = yield select(getRouterPathname)
  const pageViewData = {
    'User Type': 'asistido',
  }
  trackPage(pageViewData)
}

export default function* rootSaga() {
  yield all([
    takeLatest([actionTypes.GA_SEND_EVENT], sendGAEventSaga),
    takeLatest(
      [
        stepperActionTypes.SET_ACTIVE_STEP,
        stepperActionTypes.CLEAR_ACTIVE_STEP,
        stepperActionTypes.PRESELECTED_STEP,
        onlineChannelActionTypes.COMPLETE_ANALYTICS_ONLINE_CHANNEL,
      ],
      sendPageViewSaga
    ),
    takeLatest([stepperActionTypes.GO_NEXT_STEP], sendGACheckoutEventSaga),
    takeLatest([orderActionTypes.START_SIGN], sendGASignEventSaga),
    takeLatest([thankYouActionTypes.LOAD_THANK_YOU_PAGE], thankYouPageEventSaga),
    takeLatest([loginActionTypes.LOAD_LOGIN_PAGE], loginPageEventSaga),
    takeLatest([dashboardActionTypes.LOAD_DASHBOARD_PAGE], dashboardPageEventSaga),
  ])
}
